import * as React from "react";
import * as HoverCard from "@radix-ui/react-hover-card";

/** Find all scrollable ancestors of a node (including window) */
function getScrollParents(node) {
  const parents = [];
  if (!node || node === document) return parents;
  let el = node.parentElement;
  const overflowRegex = /(auto|scroll|overlay)/;
  while (el) {
    const style = getComputedStyle(el);
    if (
      overflowRegex.test(style.overflow + style.overflowY + style.overflowX)
    ) {
      parents.push(el);
    }
    el = el.parentElement;
  }
  parents.push(window);
  return parents;
}

export default function EllipsisTooltip({
  text,
  children,
  maxWidth = 300,
  openDelay = 60,
  closeDelay = 100,
  side = "bottom", // default: bottom
  align = "start",
  sideOffset = 8,
  collisionPadding = 8,
  cursor = "pointer", // "help" | "pointer"
}) {
  const [open, setOpen] = React.useState(false);
  const [tableHeader, setTableHeader] = React.useState(null);

  // NEW: dynamic side that flips to "top" near bottom of viewport
  const [computedSide, setComputedSide] = React.useState(side);

  const triggerRef = React.useRef(null);

  // Ensure we always give Radix a valid, focusable element for asChild
  const triggerChild = React.isValidElement(children) ? (
    React.cloneElement(children, {
      ref: triggerRef,
      tabIndex: children.props?.tabIndex ?? 0,
      className: [
        "truncate block",
        children.props?.className || "",
        cursor === "help" ? "cursor-help" : "cursor-pointer",
      ]
        .join(" ")
        .trim(),
      style: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...(children.props?.style || {}),
      },
    })
  ) : (
    <span
      ref={triggerRef}
      tabIndex={0}
      className={`truncate block ${
        cursor === "help" ? "cursor-help" : "cursor-pointer"
      } text-[#35424D] text-[13px] font-[400] outline-none focus:ring-0`}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {String(children)}
    </span>
  );

  // Locate the sticky header (closest thead in the same table) — run once after mount
  React.useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = triggerRef.current;
      if (!el) return;
      const table = el.closest("table");
      const thead = table?.querySelector("thead") || null;
      setTableHeader(thead);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // NEW: compute side based on available space below the trigger
  React.useEffect(() => {
    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // flip to top if less than 150px available below; otherwise keep the provided "side"
      setComputedSide(spaceBelow < 150 ? "top" : side);
    };

    updatePosition();
    // capture phase scroll listens to scroll on ancestors too
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [side]);

  // Close tooltip automatically on scroll of any scrollable ancestor — run once
  React.useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    const subs = [];
    const handler = () => setOpen(false);
    for (const sp of getScrollParents(el)) {
      sp.addEventListener("scroll", handler, { passive: true });
      subs.push(() => sp.removeEventListener("scroll", handler));
    }
    return () => subs.forEach((off) => off());
  }, []);

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={setOpen}
      openDelay={openDelay}
      closeDelay={closeDelay}
    >
      <HoverCard.Trigger asChild>{triggerChild}</HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side={computedSide}
          align={align}
          sideOffset={sideOffset}
          avoidCollisions={false}
          // Keep away from the sticky header and within table’s area
          collisionBoundary={tableHeader ? [tableHeader] : undefined}
          collisionPadding={{
            top: (tableHeader?.getBoundingClientRect().height || 0) + 6,
            right: collisionPadding,
            bottom: collisionPadding,
            left: collisionPadding,
          }}
          className="z-[9999] max-w-[400px] whitespace-pre-wrap break-words rounded-md
                     bg-gray-800 px-3 py-1.5 text-xs text-white shadow-lg"
          style={{ maxWidth }}
        >
          {String(text)}
          <HoverCard.Arrow className="fill-gray-800" />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
